// Voicetracking: Übersicht (Tage × Stunden: rot = offen, gelb = teilweise, grün = fertig)
// und Recorder je Slot: Ende vorher anhören, aufnehmen (Pegel), Anfang nachher anhören,
// Abfolge probehören, speichern – oder eine Datei (z. B. vom RodeCaster) hochladen.

import { useEffect, useRef, useState } from "react";
import { api, fmtMs, toast, useLoad } from "../../api";
import { Empty, HowTo, Meter, PageHead, Switch } from "../../ui";
import { VtStudio } from "./VtStudio";

const fmtClock = (iso: string) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

function Overview({ onPick }: { onPick: (hourStart: string) => void }) {
  const { data } = useLoad<any[]>("/api/vt/overview?days=7");
  if (!data) return <div className="card">Lade …</div>;
  const days = [...new Set(data.map((r) => r.day))];
  const cell = (day: string, hour: number) => data.find((r) => r.day === day && r.hour === hour);
  return (
    <div className="card" style={{ overflowX: "auto" }}>
      <h2>Übersicht</h2>
      {!data.length ? <Empty>Keine VT-Slots in den nächsten 7 Tagen. VT-Slots entstehen über das Uhren-Element „Voicetrack-Slot“ oder im Sendeplan per Rechtsklick „VT hier“.</Empty> : (
        <table style={{ borderCollapse: "separate", borderSpacing: 3 }}>
          <thead><tr><th />{Array.from({ length: 24 }, (_, h) => <th key={h} className="small muted">{h}</th>)}</tr></thead>
          <tbody>
            {days.map((d) => (
              <tr key={d}>
                <td className="small" style={{ paddingRight: 8, whiteSpace: "nowrap" }}>{new Date(`${d}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}</td>
                {Array.from({ length: 24 }, (_, h) => {
                  const c = cell(d, h);
                  const color = !c ? "#f1eee6" : c.open === 0 ? "var(--ok)" : c.done > 0 ? "var(--warn)" : "var(--bad)";
                  return <td key={h} title={c ? `${c.done} fertig, ${c.open} offen` : ""} onClick={() => c && onPick(c.hour_start)}
                    style={{ width: 22, height: 22, background: color, borderRadius: 4, cursor: c ? "pointer" : "default", textAlign: "center", color: "#fff", fontSize: 10 }}>{c?.open || ""}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Play a song segment [fromMs, toMs) of a media file; resolves when done or stopped. */
function playSegment(audio: HTMLAudioElement, mediaId: string, fromMs: number, toMs: number): Promise<void> {
  return new Promise((resolve) => {
    audio.src = `/api/media/${mediaId}/audio`;
    const stop = () => {
      audio.pause();
      audio.removeEventListener("timeupdate", tick);
      audio.removeEventListener("pause", done);
      resolve();
    };
    const tick = () => {
      if (audio.currentTime * 1000 >= toMs) stop();
    };
    const done = () => stop();
    audio.addEventListener("loadedmetadata", () => {
      audio.currentTime = fromMs / 1000;
      audio.play().catch(() => resolve());
    }, { once: true });
    audio.addEventListener("timeupdate", tick);
    audio.addEventListener("pause", done);
  });
}

function Recorder({ slot, onDone }: { slot: any; onDone: () => void }) {
  const [rec, setRec] = useState<MediaRecorder | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [level, setLevel] = useState<number | null>(null);
  const [secs, setSecs] = useState(0);
  const [overIntro, setOverIntro] = useState(true);
  const [busy, setBusy] = useState(false);
  const audio = useRef<HTMLAudioElement>(new Audio());
  const vtAudio = useRef<HTMLAudioElement>(new Audio());
  const chunks = useRef<Blob[]>([]);
  const raf = useRef<number>(0);
  const file = useRef<HTMLInputElement>(null);
  const prev = slot.prev;
  const next = slot.next;
  const nextIntro = next?.intro != null ? next.intro - (next.cue_in ?? 0) : 0;

  useEffect(() => () => {
    cancelAnimationFrame(raf.current);
    audio.current.pause();
    vtAudio.current.pause();
  }, []);

  const endBefore = () => prev && playSegment(audio.current, prev.media_id, Math.max(prev.cue_in ?? 0, (prev.start_next ?? prev.cue_out ?? prev.duration_ms) - 10_000), prev.cue_out ?? prev.duration_ms);
  const startAfter = () => next && playSegment(audio.current, next.media_id, next.cue_in ?? 0, (next.cue_in ?? 0) + Math.max(10_000, nextIntro + 3000));

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 2048;
      src.connect(an);
      const buf = new Float32Array(an.fftSize);
      const loop = () => {
        an.getFloatTimeDomainData(buf);
        const rms = Math.sqrt(buf.reduce((a, x) => a + x * x, 0) / buf.length);
        setLevel(rms > 1e-5 ? 20 * Math.log10(rms) : -100);
        raf.current = requestAnimationFrame(loop);
      };
      loop();
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "";
      const r = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 256_000 } : undefined);
      chunks.current = [];
      r.ondataavailable = (ev) => ev.data.size && chunks.current.push(ev.data);
      r.onstop = () => {
        cancelAnimationFrame(raf.current);
        setLevel(null);
        stream.getTracks().forEach((t) => t.stop());
        ctx.close();
        setBlob(new Blob(chunks.current, { type: r.mimeType || "audio/webm" }));
      };
      const t0 = Date.now();
      const timer = window.setInterval(() => setSecs(Math.round((Date.now() - t0) / 1000)), 250);
      r.addEventListener("stop", () => window.clearInterval(timer));
      r.start(250);
      setBlob(null);
      setRec(r);
    } catch {
      toast("Kein Zugriff auf das Mikrofon – bitte im Browser erlauben", true);
    }
  };
  const stop = () => {
    rec?.stop();
    setRec(null);
  };
  // Sequence: end of the previous song → recording → start of the next song.
  const preview = async () => {
    if (!blob) return;
    await endBefore();
    vtAudio.current.src = URL.createObjectURL(blob);
    await new Promise<void>((res) => {
      vtAudio.current.onended = () => res();
      vtAudio.current.play().catch(() => res());
    });
    await startAfter();
  };
  const upload = async (data: Blob) => {
    setBusy(true);
    try {
      const r = await fetch(`/api/vt/slots/${slot.id}/audio?over_intro=${overIntro ? 1 : 0}`, { method: "POST", body: data, headers: { "content-type": "application/x-radiocast-vt" }, credentials: "same-origin" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error ?? `Fehler ${r.status}`);
      toast(d.over_intro ? `Gespeichert – liegt über dem Intro des nächsten Songs (${fmtMs(d.length_ms)})` : `Gespeichert (${fmtMs(d.length_ms)})${overIntro && nextIntro ? " – zu lang fürs Intro, läuft als eigenes Element" : ""}`);
      setBlob(null);
      onDone();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card stack">
      <div className="row wrap">
        <b>{fmtClock(slot.planned_start)}</b>
        {slot.status === "unfilled" ? <span className="badge bad">offen</span> : <span className="badge ok">aufgenommen</span>}
        <span className="spacer" />
        {slot.media_id && <button className="btn small danger" onClick={async () => { await api(`/api/vt/slots/${slot.id}/audio`, { method: "DELETE" }); onDone(); }}>Aufnahme entfernen</button>}
      </div>
      <div className="grid g2">
        <div className="small"><span className="muted">davor:</span> {prev ? `${prev.artist} – ${prev.title}` : "–"}</div>
        <div className="small"><span className="muted">danach:</span> {next ? `${next.artist} – ${next.title}` : "–"} {nextIntro ? <span className="badge info">Intro {fmtMs(nextIntro)}</span> : null}</div>
      </div>
      <div className="row wrap">
        <button className="btn" disabled={!prev} onClick={endBefore}>▶ Ende vorher</button>
        {rec ? <button className="btn danger" onClick={stop}>■ Stopp ({secs} s)</button> : <button className="btn primary" onClick={start}>● Aufnehmen</button>}
        <button className="btn" disabled={!next} onClick={startAfter}>▶ Anfang nachher</button>
        <button className="btn" disabled={!blob} onClick={preview}>▶ Abfolge probehören</button>
        <button className="btn" onClick={() => { audio.current.pause(); vtAudio.current.pause(); }}>Stopp</button>
      </div>
      {level !== null && <Meter db={level} mark={-18} />}
      <div className="row wrap">
        <Switch checked={overIntro} onChange={setOverIntro} label={nextIntro ? `über das Intro des nächsten Songs legen, wenn es passt (bis ${fmtMs(nextIntro)})` : "über das Intro legen (nächster Song hat keinen Intro-Cue)"} />
        <span className="spacer" />
        <input ref={file} type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
        <button className="btn" disabled={busy} onClick={() => file.current?.click()}>⇪ Datei hochladen</button>
        <button className="btn primary" disabled={!blob || busy} onClick={() => blob && upload(blob)}>{busy ? "Speichere …" : "Speichern"}</button>
      </div>
    </div>
  );
}

export function VoicetrackPage() {
  const [hour, setHour] = useState<string | null>(null);
  const [classic, setClassic] = useState(false);
  const { data: slots, reload } = useLoad<any[]>(hour ? `/api/vt/slots?hour_start=${encodeURIComponent(hour)}` : "/api/vt/slots", [hour]);
  // The whole hour of the first slot (for the hour clock and the list beside the studio).
  const hs = hour ?? slots?.[0]?.hour_start ?? null;
  const day = hs ? new Date(hs).toLocaleDateString("sv-SE") : null;
  const { data: log } = useLoad<any>(day ? `/api/log?day=${day}` : null, [day]);
  const hourEntries = (log?.entries ?? []).filter((e: any) => hs && new Date(e.hour_start).getTime() === new Date(hs).getTime());
  return (
    <div>
      <PageHead title="Voicetracking" sub="Moderationen vorproduzieren" />
      <HowTo text="Moderationen vorproduzieren, die genau an ihrer Stelle im Sendeplan landen – mit dem Ende des Titels davor und dem Anfang danach im Ohr." chapter="voicetracking" />
      <div className="stack">
        <Overview onPick={setHour} />
        <div className="row">
          <h2 style={{ margin: 0 }}>{hour ? `Stunde ${new Date(hour).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : "Nächste offene Slots"}</h2>
          <span className="spacer" />
          {hour && <button className="btn small" onClick={() => setHour(null)}>Alle offenen</button>}
          <Switch checked={classic} onChange={setClassic} label="Einfache Ansicht" />
        </div>
        {!slots?.length ? <Empty>Keine Slots.</Empty> : classic ? slots.map((s) => <Recorder key={s.id} slot={s} onDone={reload} />) : (
          <VtStudio key={hs ?? "x"} slots={slots} hourEntries={hourEntries} onChanged={reload} />
        )}
      </div>
    </div>
  );
}
